import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { STORAGE_KEYS, getItem, removeItem, setItem } from './storage';

/**
 * BASE_URL behavior:
 * - Dev: empty string → calls go to /api/v1/... (Vite proxy fwd to backend, no CORS)
 * - Native (Capacitor) / prod: set VITE_API_URL to absolute https URL
 */
const BASE_URL = import.meta.env['VITE_API_URL'] ?? '';

const COMPANY_BY_TENANT: Record<string, string> = {
  WITTY: '1000',
  PSP: '2000',
  YPC: '3000',
};

export const api = axios.create({
  baseURL: `${BASE_URL}/api/v1`,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    // Backend CsrfHeaderGuard requires this on all state-changing requests
    // (POST/PUT/PATCH/DELETE). See @gpl/constants X_REQUESTED_WITH_VALUE.
    'X-Requested-With': 'XMLHttpRequest',
  },
});

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const [token, tenant] = await Promise.all([
    getItem(STORAGE_KEYS.ACCESS_TOKEN),
    getItem(STORAGE_KEYS.TENANT),
  ]);
  if (token) config.headers.set('Authorization', `Bearer ${token}`);
  // Backend tenant routing: matches the middleware that reads x-company-id
  if (tenant) {
    const companyId = COMPANY_BY_TENANT[tenant] ?? tenant;
    config.headers.set('x-company-id', companyId);
  }
  return config;
});

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

interface RefreshResponse {
  access_token: string;
  refresh_token?: string;
}

let isRefreshing = false;
let refreshQueue: Array<(token: string | null) => void> = [];

function flushQueue(token: string | null): void {
  const callbacks = refreshQueue;
  refreshQueue = [];
  callbacks.forEach((cb) => cb(token));
}

async function performRefresh(): Promise<string | null> {
  const refreshToken = await getItem(STORAGE_KEYS.REFRESH_TOKEN);
  if (!refreshToken) return null;
  try {
    const resp = await axios.post<{ code?: number; data?: RefreshResponse } | RefreshResponse>(
      `${BASE_URL}/api/v1/auth/refresh`,
      { refresh_token: refreshToken },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        timeout: 15000,
      },
    );
    const body = resp.data as { code?: number; data?: RefreshResponse } | RefreshResponse;
    const payload =
      body !== null && typeof body === 'object' && 'data' in body && 'code' in body
        ? (body as { data: RefreshResponse }).data
        : (body as RefreshResponse);
    if (!payload?.access_token) return null;
    await setItem(STORAGE_KEYS.ACCESS_TOKEN, payload.access_token);
    if (payload.refresh_token) {
      await setItem(STORAGE_KEYS.REFRESH_TOKEN, payload.refresh_token);
    }
    return payload.access_token;
  } catch {
    return null;
  }
}

api.interceptors.response.use(
  (res) => {
    const body = res.data as unknown;
    if (
      body !== null &&
      typeof body === 'object' &&
      'code' in body &&
      'data' in body
    ) {
      res.data = (body as { data: unknown }).data;
    }
    return res;
  },
  async (err: AxiosError) => {
    const originalRequest = err.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean; _skipAuthRefresh?: boolean })
      | undefined;

    if (
      err.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest._skipAuthRefresh &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/logout')
    ) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push((token) => {
            if (!token) {
              reject(err);
              return;
            }
            originalRequest.headers.set('Authorization', `Bearer ${token}`);
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;
      try {
        const newToken = await performRefresh();
        if (!newToken) {
          flushQueue(null);
          await removeItem(STORAGE_KEYS.ACCESS_TOKEN);
          await removeItem(STORAGE_KEYS.REFRESH_TOKEN);
          onUnauthorized?.();
          return Promise.reject(err);
        }
        flushQueue(newToken);
        originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
        return api(originalRequest);
      } finally {
        isRefreshing = false;
      }
    }

    if (err.response?.status === 401) {
      await removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      await removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      onUnauthorized?.();
    }
    return Promise.reject(err);
  },
);

/**
 * Toggle: when VITE_USE_REAL_API is set, hooks call backend; otherwise mocks.
 * Default off — local dev works without backend running.
 */
export const USE_REAL_API = import.meta.env['VITE_USE_REAL_API'] === '1';
